
import { defineComponent, ref, reactive, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import API from "@/apis/TPSAPI";
import { APIResultCode } from "@/models/enums/APIResultCode";

// element-plus
import { ElMessage } from "element-plus";
// element-plus icons
import { Search, EditPen, Delete, Calendar } from "@element-plus/icons-vue";

/** Utils */
import { usePaginatorSetup } from "@/stores/PaginatorStore";
import IReqProjectSearchCriteriaDTO from "@/models/dto/IReqProjectSearchCriteriaDTO";
import IResProjectInfoDTO from "@/models/dto/IResProjectInfoDTO";
import ProjectSearchCriteria from "@/models/ProjectSearchCriteria";
import ElTable from "element-plus/lib/components/table";
import IResSiteInfoDTO from "@/models/dto/IResSiteInfoDTO";
import { compare, dateFormat, delay } from "@/plugins/utility";
import { DataTableType } from "@/models/enums/DataTableType";

export default defineComponent({
  emits: ["ProjectSelect"],
  setup(prpos, context) {
    const { t } = useI18n();
    const paginatorSetup = usePaginatorSetup();
    const tableType = DataTableType.projectSearchTable;

    const columns: any = [
      { prop: "pjCode", visiable: true, label: t("PJCode") },
      { prop: "siteName", visiable: true, label: t("factories") },
      { prop: "factoryName", visiable: true, label: t("factorys") },
    ];
    const state = reactive({
      isTableLoading: false,
      searchCriteria: new ProjectSearchCriteria(),
      filteredProjects: [] as IResProjectInfoDTO[],
      displayProjects: [] as IResProjectInfoDTO[],
      isSiteLoading: false,
      tableRadio: undefined as number | undefined,

      // pagination
      currentPage: 1,
      pageSize: 20,

      // 廠區/工廠選擇清單
      sites: [] as IResSiteInfoDTO[],

      colDisplayCtrl: new Array(50).fill(true) as boolean[],
      columns: columns, //試驗中
    });

    // #region 建立表單ref與Validator
    const projectTableRef = ref<InstanceType<typeof ElTable>>();
    // #endregion

    // #region Hook functions
    // #endregion

    //#region UI Events
    /** 按下搜尋 */
    const onSearchProjectClicked = async () => {
      try {
        state.isTableLoading = true;

        //清除table排序
        projectTableRef.value?.clearSort();
        await filterProjectListAsync();
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };
    /** 按下並放開Enter鍵 */
    const onInputFieldEnterKeyUp = async () => {
      await onSearchProjectClicked();
    };

    /** search 清除 */
    const onClearSearchCriteriaClicked = () => {
      state.searchCriteria.clear();
    };

    /** 顯示廠區下拉選單時 */
    const onSiteListShown = async () => {
      try {
        state.isSiteLoading = true;
        await updateSite();
      } catch (error) {
        console.log(error);
      } finally {
        state.isSiteLoading = false;
      }
    };
    let a = t("hello");

    /** 當專案被選擇時 */
    const onProjectSelectChanged = async (project: IResProjectInfoDTO) => {
      if (!project) return;
      console.log(`onProjectSelectChanged.  ${project.id}, ${project.pjCode}`);
      state.tableRadio = project.id;
      context.emit("ProjectSelect", project);
    };

    /** 改變排序 */
    const onSortChanged = (sort: any) => {
      console.log(`onSortChanged: ${sort.column}, ${sort.prop}, ${sort.order}`);
      try {
        state.isTableLoading = true;
        state.filteredProjects.sort(compare(sort.prop, sort.order));
        updateDisplayList();
      } catch (error) {
        console.log(error);
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 分頁輸入 */
    const onPagesizeChanged = () => {
      updateDisplayList();
    };

    const onCurrentPageChanged = () => {
      updateDisplayList();
    };

    /** 關閉欄位顯示設定Panel時 */
    const onColDisplayCtrlPanelHidded = async () => {
      await API.patchTableDisplayPreference(tableType, { displayMap: state.colDisplayCtrl });
    };

    //#endregion

    //#region Private Functions
    /** 供外部呼叫 : 當顯示dialog的時候會呼叫此方法，用以重置搜尋結果 */
    const resetSearch = async () => {
      try {
        state.isTableLoading = true;
        await updateUserPreference();
        await updateSite();
        await filterProjectListAsync();
        state.tableRadio = undefined;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 更新使用者設置偏好 */
    const updateUserPreference = async () => {
      const getPrefResponse = await API.getTableDisplayPreference(tableType);
      if (getPrefResponse.data.result !== 1) {
        console.log(`updateUserPreference : ${getPrefResponse.data.msg}`);
      }
      state.colDisplayCtrl = getPrefResponse.data.content;
    };

    /** 呼叫API, 更新廠區清單 */
    const updateSite = async () => {
      try {
        const getSiteResult = await API.getAllSites();

        if (getSiteResult.data.result !== APIResultCode.success) {
          throw new Error(getSiteResult.data.msg);
        }

        state.sites = getSiteResult.data.content;
      } catch (error) {
        console.log(error);
      }
    };

    /** 更新搜尋結果 */
    const filterProjectListAsync = async () => {
      if (process.env.VUE_APP_RUN_ENV === "DEV") await delay(1000);

      let searchCriteriaDTO = state.searchCriteria.ToReqDTO();
      searchCriteriaDTO.startDate = undefined;
      searchCriteriaDTO.endDate = undefined;
      searchCriteriaDTO.includeDate = new Date();

      await getFilteredProjects(searchCriteriaDTO);

      updateDisplayList();
    };

    /** 呼叫API, 取得搜尋結果 */
    const getFilteredProjects = async (searchCriteria: IReqProjectSearchCriteriaDTO) => {
      /* 若 開發時 等待 1 秒 */
      if (process.env.VUE_APP_RUN_ENV === "DEV") await delay(1000);

      const projectSearchResult = await API.searchProject(searchCriteria);
      if (projectSearchResult.data.result !== APIResultCode.success) {
        throw new Error(projectSearchResult.data.msg);
      }
      state.filteredProjects = projectSearchResult.data.content;
    };

    /** 更新顯示清單 */
    const updateDisplayList = () => {
      state.displayProjects = state.filteredProjects.slice((state.currentPage - 1) * state.pageSize, state.currentPage * state.pageSize);
    };
    //#endregion

    return {
      ...toRefs(state),
      paginatorSetup,
      t,

      // icons
      Search,
      EditPen,
      Delete,
      Calendar,

      // function
      getFilteredProjects,
      updateDisplayList,

      onSearchProjectClicked,
      onClearSearchCriteriaClicked,
      onPagesizeChanged,
      onCurrentPageChanged,
      onSortChanged,
      onSiteListShown,
      onProjectSelectChanged,
      onInputFieldEnterKeyUp,
      onColDisplayCtrlPanelHidded,
      resetSearch,

      // date
      dateFormat,
    };
  },
});
